import axios from './axiosService';
import jssha from '../../../plugins/jssha';
import DOMPurify from 'dompurify';

function createKey(presentation) {
  return jssha.sha1(`${presentation.id}.${presentation.files_hash}.${presentation.content_updated_at}`)
}

const getToken = async (presentation, config) => {
  const form = {
    presentation_uuid: presentation.uuid,
    config
  }
  const {data} = await axios.post(`/api/onlyoffice/token`, form)
  return data.token;
};

/**
 * MLD-4596
 */
export default {
  async initDocEditor(config) {
    console.log('initOnlyOffice', config);
    return new window.DocsAPI.DocEditor('onlyoffice_viewer', config);
  },
  /**
   * MLD-7956
   * @param {object} team - The Team object
   * @param {object} user - The User object
   * @param {object} presentation - The presentation object
   * @param {object} file - The file object
   * @param {string} mode
   * @param {string} documentType - word, cell, slide, pdf, diagram. @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/#documenttype
   * @param {function} callbackURL
   * @returns {Promise<{document: {fileType: (*|string), isForm: boolean, key, permissions: {chat: boolean, comment: boolean, copy: boolean, download: boolean, fillForms: boolean, modifyContentControl: boolean, modifyFilter: boolean, print: boolean, review: boolean}, title, url}, documentType, editorConfig: {callbackUrl, mode, recent: *[], region: string, templates: *[], user: {group, id, image: (*|null), name}, customization: {autosave: boolean, close: {visible: boolean, text: string}, comments: boolean, compactHeader: boolean, compactToolbar: boolean, compatibleFeatures: boolean, customer: {address: string, info: string, logo: string, logoDark: string, mail: string, name: string, phone: null, www: string}, features: {featuresTips: boolean, roles: boolean, spellcheck: {mode: boolean, change: boolean}, tabBackground: {mode: string, change: boolean}, tabStyle: {mode: string, change: boolean}}, forcesave: boolean, forceWesternFontSize: boolean, help: boolean, hideNotes: boolean, hideRightMenu: boolean, hideRulers: boolean, integrationMode: string, logo: {image: string, imageDark: string, url: null, visible: boolean}, macros: boolean, macrosMode: string, mentionShare: boolean, mobile: {forceView: boolean, info: boolean, standardView: boolean}, plugins: boolean, pointerMode: string, review: {hideReviewDisplay: boolean, hoverMode: boolean, reviewDisplay: string, showReviewChanges: boolean, trackChanges: boolean}, showHorizontalScroll: boolean, showVerticalScroll: boolean, submitForm: boolean, toolbarHideFileName: boolean, uiTheme: string, zoom: number, about: boolean, layout: {header: {editMode: boolean, save: boolean, user: boolean, users: boolean}, leftMenu: {mode: boolean, navigation: boolean, spellcheck: boolean}, rightMenu: {mode: boolean}, statusBar: boolean, toolbar: {collaboration: boolean, draw: boolean, file: boolean, home: {mailmerge: boolean}, layout: boolean, plugins: boolean, protect: boolean, references: boolean, save: boolean, view: {navigation: boolean}}}, loaderName: string}}, events: {onAppReady: config.events.onAppReady, onInfo: config.events.onInfo, onMakeActionLink: config.events.onMakeActionLink, onDocumentReady: config.events.onDocumentReady, onDocumentStateChange: config.events.onDocumentStateChange, onError: config.events.onError, onRequestClose: config.events.onRequestClose, onRequestCreateNew: config.events.onRequestCreateNew, onRequestHistory: config.events.onRequestHistory, onRequestHistoryClose: config.events.onRequestHistoryClose, onRequestHistoryData: config.events.onRequestHistoryData, onRequestInsertImage: config.events.onRequestInsertImage, onRequestMailMergeRecipients: config.events.onRequestMailMergeRecipients, onRequestOpen: config.events.onRequestOpen, onRequestReferenceData: config.events.onRequestReferenceData, onRequestReferenceSource: config.events.onRequestReferenceSource, onRequestRefreshFile: config.events.onRequestRefreshFile, onRequestRename: config.events.onRequestRename, onRequestRestore: config.events.onRequestRestore, onRequestSaveAs: config.events.onRequestSaveAs, onRequestSelectDocument: config.events.onRequestSelectDocument, onRequestSelectSpreadsheet: config.events.onRequestSelectSpreadsheet, onRequestSendNotify: config.events.onRequestSendNotify, onRequestStartFilling: config.events.onRequestStartFilling, onRequestUsers: config.events.onRequestUsers, onSubmit: config.events.onSubmit, onUserActionRequired: config.events.onUserActionRequired, onWarning: config.events.onWarning, height: string, token: null, type: string, width: string}}>}
   */
  async initConfig(team, user, presentation, file, mode, documentType, callbackURL) {
    const key = createKey(presentation);
    const config = { // @see https://api.onlyoffice.com/editors/basic
      document: {
        fileType: file ? file.extension : 'pptx',
        isForm: false,
        key: key,
        permissions: { // @see https://api.onlyoffice.com/editors/config/document/permissions
          chat: false,
          comment: false, // @see https://api.onlyoffice.com/editors/config/document/permissions#comment
          copy: false, // @see https://api.onlyoffice.com/editors/config/document/permissions#copy
          download: false, // @see https://api.onlyoffice.com/editors/config/document/permissions#download
          //edit: false, // @see https://api.onlyoffice.com/editors/config/document/permissions#edit
          fillForms: false, // @see https://api.onlyoffice.com/editors/config/document/permissions#fillForms
          modifyContentControl: false,
          modifyFilter: false,
          print: false, // @see https://api.onlyoffice.com/editors/config/document/permissions#print
          review: false, // @see https://api.onlyoffice.com/editors/config/document/permissions#review
        },
        title: presentation.name,
        //url: 'https://teams.mobilelocker.com/demo_team/assets/Allergan%20Aesthetics%20Pipeline%20Overview%20Deck_Feb.%202022_US%20Approved%20Version.pptx',
        url: file.url,
      },

      documentType: documentType,
      editorConfig: { // @see https://api.onlyoffice.com/editors/config/editor
        // Specifies the data received from the document editing service using the onMakeActionLink event or the onRequestSendNotify event in data.actionLink parameter, which contains the information about the action in the document that will be scrolled to.
        /*actionLink: {

        },*/
        // Specifies absolute URL to the document storage service (which must be implemented by the software integrators who use ONLYOFFICE Docs on their own server).
        callbackUrl: callbackURL,
        /*coEditing: {
          mode: "fast",
          change: true,
        },*/
        //createUrl: "https://example.com/url-to-create-document/",
        // lang: "en",
        // Defines the editor opening mode. Can be either view to open the document for viewing, or edit to open the document in the editing mode allowing to apply changes to the document data. The default value is "edit".
        // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/#mode
        mode: mode,
        // Defines the presence or absence of the documents in the Open Recent... menu option.
        // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/#recent
        recent: [],
        region: 'en-US',
        // Defines the presence or absence of the templates in the Create New... menu option.
        // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/#templates
        templates: [],
        // Defines the user currently viewing or editing the document.
        // https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/#user
        user: {
          // The group (or several groups separated with commas) the user belongs to.
          group: team.name,
          // The identification of the user. The length is limited to 128 symbols. This information is stored and used to distinguish co-authors, indicate the author of the last changes when saving and highlighting history (in the list of changes), and count users with access for a license based on the number of users. We recommend using some unique anonymized hash. Do not use sensitive data, like name or email for this field.
          id: user.uuid,
          // The path to the user's avatar.
          image: user.photo_url ?? null,
          // The full name of the user. The length is limited to 128 symbols. Used since version 4.2.
          name: user.name,
        },

        // CUSTOMIZATION OPTIONS
        customization: {
          // STANDARD BRANDING OPTIONS
          // The customization section allows you to customize the editor interface according to your needs, as well as change the presence or absence of additional buttons, links,
          // change logos and other details of the editor's owner.
          // On this page you will find the customization parameters available for the standard branding of ONLYOFFICE Docs Developer. If you have an extended white label license,
          // please visit the White label page to find out what additional customization options are available.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/

          // Adds a request for the anonymous name.
          /*anonymous: {
            request: true,
            label: "Guest",
          },*/

          // Defines if the Autosave menu option is enabled or disabled. If set to false, only Strict co-editing mode can be selected, as Fast does not work without autosave.
          // The default value is true.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#autosave
          autosave: true,

          // Defines settings for the cross button to close the editor.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#close
          close: {
            // Defines if the cross button to close the editor is displayed or hidden. The default value is true.
            visible: false,
            // Defines if the cross button to close the editor is displayed or hidden. The default value is true.
            text: 'Close file',
          },

          // Defines if the Comments menu button is displayed or hidden. Please note that in case you hide the Comments button,
          // the corresponding commenting functionality will be available for viewing only, adding and editing comments will be unavailable. The default value is true.
          // @see https://api.onlyoffice.com/editors/config/editor/customization#comments
          comments: false,

          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#compactheader
          // Defines if the additional action buttons are displayed in the upper part of the editor window header next to the logo (false) or in
          // the toolbar (true) making the header more compact. The default value is false.
          compactHeader: false,

          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#compacttoolbar
          // Defines if the top toolbar type displayed is full (false) or compact (true). The default value is false.
          // Starting from version 8.3, this setting is also available for the viewer. The default value for the view mode is true.
          compactToolbar: true,
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#compatiblefeatures
          // Defines the use of functionality only compatible with the OOXML format. For example, do not use comments on the entire document. The default value is false.
          compatibleFeatures: false,
          customer: { // @see https://api.onlyoffice.com/editors/config/editor/customization#customer
            address: '72 North Main Street, Suite 202, Hudson, OH 44236 USA',
            info: 'Smarter HCP Engagement',
            logo: 'https://teams.mobilelocker.com/demo_team/assets/mobilelocker_logo.png',
            logoDark: 'https://teams.mobilelocker.com/demo_team/assets/mobilelocker_logo_white.png',
            mail: 'support@mobilelocker.com',
            name: 'Mobile Locker',
            phone: null,
            www: 'www.mobilelocker.com',
          },
          features: {
            featuresTips: true,
            roles: true,
            spellcheck: {
              mode: true,
              change: true,
            },
            tabBackground: {
              mode: "header",
              change: true,
            },
            tabStyle: {
              mode: "fill",
              change: true,
            },
          },
          /*feedback: {
            url: "https://example.com",
            visible: true,
          },*/
          // Adds the request for the file force saving to the callback handler when saving the document within the document editing service (e.g. clicking the Save button, etc.). The default value is false.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#forcesave
          forcesave: false,
          // Defines if the Western (true) or Chinese (false) font size is used in the Chinese (Simplified) UI. The default value is false.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#forcewesternfontsize
          forceWesternFontSize: false,
          // Defines settings for the Open file location menu button and upper right corner button.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#goback
          /*goback: {
            // Opens the website in the new browser tab/window (if the value is set to true) or the current tab (if the value is set to false) when the Open file location button is clicked. The default value is true.
            blank: true,
            text: "Open file location",
            url: "https://example.com",
          },*/
          // Defines if the Help menu button is displayed or hidden. The default value is true.
          help: true,
          // Defines if the note panel is displayed or hidden on first loading. The default value is false. This parameter is available for the presentation editor only.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#hidenotes
          hideNotes: false,
          // Defines if the right menu is displayed or hidden on first loading. The default value is true.
          hideRightMenu: true,
          // Defines if the editor rulers are displayed or hidden. This parameter is available for the document and presentation editors. The default value is false for the document editor and true for presentations.
          hideRulers: false,
          // Defines the mode of embedding editors into the web page. The embed value disables scrolling to the editor frame when it is loaded as the focus is not captured.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#integrationmode
          integrationMode: "embed",
          // Changes the image file at the top left corner of the editor header. The recommended image height is 20 pixels.
          logo: {
            // Path to the image file used to show in the common work mode (i.e. in view and edit modes for all editors)
            // or in the embedded mode (see the config section to find out how to define the embedded document type).
            // The image must have the following size: 172x40.
            image: 'https://teams.mobilelocker.com/demo_team/assets/mobilelocker_logo.png',
            // Path to the image file used for the dark header (for example, in a dark
            // theme or in a theme with a colored header).
            // The image must have the following size: 172x40.
            imageDark: 'https://teams.mobilelocker.com/demo_team/assets/mobilelocker_logo_white.png',
            //imageLight: "https://example.com/light-logo.png",
            // The absolute URL which will be used when someone clicks the logo image (can be used to go to your web site, etc.). Leave as an empty string or null to make the logo not clickable.
            url: null,
            // Shows or hides the logo. The default value is true.
            visible: false,
          },
          // Defines if document macros will be automatically run when the editor opens. The default value is true. The false value hides the macros settings from the user.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#macros
          macros: false,
          // Defines the macros run mode when autostart is enabled. Can take the following values:
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#macrosmode
          macrosMode: 'disable',
          // Defines the hint that describes the event after mentions in a comment. If true, a hint indicates that the user will receive a notification and access to the document.
          // If false, a hint indicates that the user will receive only a notification of the mention. The default value is true.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#macrosmode
          mentionShare: false,
          // Defines the mobile document editor settings.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#mobile
          mobile: {
            // Defines whether the view mode is enabled on launch in the mobile document editor. The default value is true.
            forceView: true,
            // Defines whether the Document Info button is displayed or hidden in the mobile document editor. The default value is false.
            info: false,
            // Defines whether the editor will be opened in Standard view instead of Mobile view. The default value is false.
            standardView: false,
          },
          // Defines if plugins will be launched and available. The default value is true.
          plugins: false,
          // Defines the pointer mode (select or hand) when the presentation editor is loaded in the viewer. The default value is select.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#pointermode
          pointerMode: 'select',
          // Contains the information about the review mode.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#review
          review: {
            // Defines if the Display mode button is displayed or hidden on the Collaboration tab. The default value is false.
            hideReviewDisplay: false,
            // Defines the review display mode: show reviews in tooltips by hovering the changes (true) or in balloons by clicking the changes (false). The default value is false.
            hoverMode: false,
            // Defines the review editing mode which will be used when the document is opened for viewing. It will only be available for the document
            // editor if mode is set to view.Can take the following values:
            reviewDisplay: "original",
            // Defines if the review changes panel is automatically displayed or hidden when the editor is loaded. The default value is false.
            showReviewChanges: false,
            // Defines if the document is opened in the review editing mode (true) or not (false) regardless of the document.permissions.review parameter
            // (the review mode is changed only for the current user). If the parameter is undefined, the document.permissions.review value is
            // used (for all the document users).
            trackChanges: true,
          },
          // Starting from version 8.3, defines if the horizontal scroll is automatically displayed or hidden when the spreadsheet editor is loaded. The default value is true.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#showhorizontalscroll
          showHorizontalScroll: true,
          // Starting from version 8.3, defines if the vertical scroll is automatically displayed or hidden when the spreadsheet editor is loaded. The default value is true.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#showverticalscroll
          showVerticalScroll: true,
          // Starting from version 8.3, defines the background color for the slide show in the presentation editor. Can be represented in the HEX, RGB, or RGBA formats. For example, #ff0000, rgb(255, 0, 0), rgba(255, 0, 0, 0.5).
          //slidePlayerBackground: '#ff0000',
          // Starting from version 8.3, defines the Complete & Submit button settings. If this parameter is a boolean value, then it specifies whether
          // the Complete & Submit button will be displayed or hidden on the top toolbar. Button will only be available for the pdf format. The default value is true.
          submitForm: false,
          /*submitForm: {
            // Defines whether the Complete & Submit button will be displayed or hidden on the top toolbar. Button will only be available for the pdf format. The default value is true.
            visible: true,
            // Defines a message displayed after forms are submitted. The following values are available:
            resultMessage: "text",
          },*/
          // Defines if the document title is visible on the top toolbar (false) or hidden (true). The default value is false.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#toolbarhidefilename
          toolbarHideFileName: true,
          // Defines the editor theme settings. It can be set in two ways:
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#uitheme
          uiTheme: 'theme-white',
          // Starting from version 8.3, defines the HEX color for the default heading styles in the document editor.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#wordheadingscolor
          // wordHeadingsColor: '#00ff00',
          // Defines the document display zoom value measured in percent. Can take values larger than 0. For text documents and presentations it is possible to set this parameter to -1
          // (fitting the document to page option) or to -2 (fitting the document page width to the editor page). The default value is 100.
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-standard-branding/#zoom
          zoom: 100,
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-white-label/#layout
          // Defines the parameters that the user can use to hide the interface elements but not to disable features completely
          // (for example, if this functionality is available from other elements such as context menu, or via hotkeys).

          // WHITE LABEL CUSTOMIZATIONS
          // allows to customize the editor interface so that it looked like your other products (if there are any) and change the presence or absence of the additional buttons, links, change logos and editor owner details;
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-white-label/#about
          // Defines if the About menu button is displayed or hidden. The default value is true.
          about: false,
          /*font: {
            name: "Arial",
            size: "11px",
          },*/
          // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-white-label/
          layout: {
            // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-white-label/#layoutheader
            // Defines the editor header settings.
            header: {
              // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/editor/customization/customization-white-label/#layoutheadereditmode
              // Defines if a button for switching editor modes will be displayed in the header or not. The default value is true.
              editMode: true,
              // Defines if the Save button in the editor header is displayed or hidden. The default value is true. Please note that this setting is used when the compactHeader parameter is set to false.
              save: true,
              // Defines if the icon with the user's avatar/initials in the editor header is displayed or hidden. The default value is true.
              user: true,
              // Defines if the button with the editing users is displayed or hidden. The default value is true.
              users: true,
            },
            // Defines the left menu settings. If this parameter is a boolean value, then it specifies whether the left menu will be displayed or hidden. The default value is true.
            leftMenu: {
              // Defines the initial value of the left panel visibility - displayed or hidden. It is used for the Left panel menu option on the View tab. The default value is true.
              mode: true,
              // Defines if the Navigation button is displayed or hidden. The default value is true. This parameter will only be available for the document editor.
              navigation: true,
              // Defines if the Spellcheck button is displayed or hidden. The default value is true. This parameter will only be available for the spreadsheet editor.
              spellcheck: true,
            },
            // Defines the right menu settings. If this parameter is a boolean value, then it specifies whether the right menu will be displayed or hidden. The default value is true.
            rightMenu: {
              // Defines the initial value of the right panel visibility - displayed or hidden. It is used for the Right panel menu option on the View tab. The default value is true.
              mode: true,
            },
            // Defines the status bar settings. If this parameter is a boolean value, then it specifies whether the status bar will be displayed or hidden. The default value is true.
            /*statusBar: {
              // Defines if an action status is displayed or hidden. The default value is true.
              actionStatus: true,
              // Defines if a button for choosing the document language is displayed or hidden. The default value is true. This parameter will only be available for the document editor and the presentation editor.
              docLang: true,
              // Defines if a button for choosing the text language is displayed or hidden. The default value is true. This parameter will only be available for the document editor and the presentation editor.
              textLang: true,
            },*/
            statusBar: false,
            // Defines the toolbar settings. If this parameter is a boolean value, then it specifies whether the toolbar will be displayed or hidden. The default value is true.
            toolbar: {
              // Defines the Collaboration tab settings. If this parameter is a boolean value, then it specifies whether the Collaboration tab will be displayed or hidden. The default value is true.
              collaboration: false,
              /*collaboration: {
                // Defines if the button for choosing the mail merge base is displayed or hidden. The default value is true.
                mailmerge: true,
              },*/
              // Defines if the Draw tab is displayed or hidden. The default value is true.
              draw: true,
              // Defines the File tab settings. If this parameter is a boolean value, then it specifies whether the File tab will be displayed or hidden. The default value is true.
              file: false,
              /*file: {
                // Defines if the Close menu option is displayed or hidden. The default value is true.
                close: true,
                // Defines if the Document info option is displayed or hidden. The default value is true.
                info: true,
                // Defines if the Save option is displayed or hidden. The default value is true.
                save: true,
                // Defines if the Advanced settings option is displayed or hidden. The default value is true.
                settings: true,
              },*/
              // Defines the Home tab settings. This tab cannot be hidden.
              home: {
                // Defines if the button for choosing the mail merge base is displayed or hidden. This parameter is deprecated, please use the toolbar.collaboration.mailmerge parameter instead.
                mailmerge: true,
              },
              // Defines if the Layout tab is displayed or hidden. This parameter will only be available for the document editor and the spreadsheet editor. The default value is true.
              layout: true,
              // Defines if the Plugins tab is displayed or hidden. The default value is true.
              plugins: true,
              // Defines if the Protection tab is displayed or hidden. The default value is true.
              protect: true,
              // Defines if the References tab is displayed or hidden. This parameter will only be available for the document editor. The default value is true.
              references: true,
              // Defines if the Save button on the toolbar is displayed or hidden. The default value is true. Please note that this setting is used when the compactHeader parameter is set to true.
              save: true,
              // Defines the View tab settings. If this parameter is a boolean value, then it specifies whether the View tab will be displayed or hidden. The default value is true.
              view: {
                // Defines if the Navigation button is displayed or hidden. The default value is true. This parameter will only be available for the document editor.
                navigation: true,
              },
            },
          },
          // Defines the path to the image logo which will be displayed while the document is being loaded (there are no special recommendations for this file, but it would be better if it
          // were in .png format with transparent background). The image will be proportionally resized to the height of 160 pixels when displayed in the editors.
          // loaderLogo: "https://example.com/loader-logo.png",
          // Defines the text which will be displayed while the document is being loaded.
          loaderName: `Loading ${DOMPurify.sanitize(presentation.name)}, please wait...`,
        },
      },
      events: { // @see https://api.onlyoffice.com/editors/config/events
        onAppReady: () => {
          console.log('OnlyOffice: onAppReady');
        },
        onInfo: (event) => {
          console.log(`OnlyOffice: onInfo - opened in ${event.data.mode} mode.`);
        },
        onMakeActionLink: (event) => {
          console.log('OnlyOffice: onMakeActionLink', event.data);
        },
        onMetaChange: (event) => {
          console.log('OnlyOffice: onMetaChange', event.data);
        },
        onPluginsReady: () => {
          console.log('OnlyOffice: onPluginsReady');
        },
        onDocumentReady: () => {
          console.log('OnlyOffice: onDocumentReady');
        },
        onDocumentStateChange: (event) => {
          console.log('OnlyOffice: onDocumentStateChange', event.data);
        },
        onError: (event) => {
          console.log('OnlyOffice: onError', event.data);

          const errorCode = event.data.errorCode;
          const errorDescription = DOMPurify.sanitize(event.data.errorDescription)
          const message = `Presentation mode failed. Error code: ${errorCode}, message: ${errorDescription}`;
          this.error = {
            message: message,
          }
        },
        onRequestClose: () => {
          console.log('OnlyOffice: onRequestClose');
          this.docEditor?.destroyEditor();
        },
        onRequestCreateNew: () => {
          console.log('OnlyOffice: onRequestCreateNew');
        },
        /*onRequestEditRights: () => {
          console.log('OnlyOffice: onRequestEditRights');
        },*/
        onRequestHistory: () => {
          console.log('OnlyOffice: onRequestHistory');
        },
        onRequestHistoryClose: () => {
          console.log('OnlyOffice: onRequestHistoryClose');
        },
        onRequestHistoryData: (event) => {
          console.log('OnlyOffice: onRequestHistoryData', event.data);
        },
        onRequestInsertImage: (event) => {
          console.log('OnlyOffice: onRequestInsertImage', event.data);
        },
        /**
         * The function called when the user is trying to open an external link by clicking the Open source button.
         * If the method is not declared, this button will not be displayed.
         * @param event
         */
        /*onRequestOpen: (event) => {
          console.log('OnlyOffice: onRequestOpen', event.data);
        },*/
        onRequestReferenceData: (event) => {
          console.log('OnlyOffice: onRequestReferenceData', event.data);
        },
        onRequestReferenceSource: (event) => {
          console.log('OnlyOffice: onRequestReferenceSource', event.data);
        },
        /**
         * @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/events/#onrequestrefreshfile
         * @param event
         */
        onRequestRefreshFile: (event) => {
          console.log('OnlyOffice: onRequestRefreshFile');
        },
        /**
         * @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/events/#onrequestrename
         * @param event
         */
        onRequestRename: (event) => {
          console.log('OnlyOffice: onRequestRename', event.data);
        },
        /**
         * @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/events/#onrequestrestore
         * @param event
         */
        onRequestRestore: (event) => {
          console.log('OnlyOffice: onRequestRestore', event.data);
        },
        onRequestSaveAs: (event) => {
          console.log('OnlyOffice: onRequestSaveAs', event.data);
        },
        onRequestSelectDocument: () => {
          console.log('OnlyOffice: onRequestSelectDocument');
        },
        onRequestSelectSpreadsheet: () => {
          console.log('OnlyOffice: onRequestSelectSpreadsheet');
        },
        onRequestSendNotify: (event) => {
          console.log('OnlyOffice: onRequestSendNotify', event.data);
        },
        /**
         * The function called when the user is trying to manage document access rights by clicking Change access rights button.
         *
         * When the access rights is changed, you must call the setSharingSettings method to update the information about the settings which allow to share the document with other users.
         * If the method is not declared the Change access rights button will not be displayed.
         * @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/events/#onrequestsharingsettings
         */
        /*onRequestSharingSettings: () => {
          console.log('OnlyOffice: onRequestSharingSettings');
        },*/
        onRequestStartFilling: () => {
          console.log('OnlyOffice: onRequestStartFilling');
        },
        onRequestUsers: (event) => {
          console.log('OnlyOffice: onRequestUsers', event.data);
        },
        onSubmit: (event) => {
          console.log('OnlyOffice: onSubmit', event.data);
        },
        onUserActionRequired: () => {
          console.log('OnlyOffice: onUserActionRequired');
        },
        /**
         * The function called when a warning occurs. The warning message is sent in the data parameter. A list of error codes can be found here.
         * @see https://github.com/ONLYOFFICE/sdkjs/blob/master/common/errorCodes.js
         * @param event
         */
        onWarning: (event) => {
          console.log('OnlyOffice: onWarning', event.data);
        },
        // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/#height
        // Defines the document height (100% by default) in the browser window.
        height: "100%",
        // @see https://api.onlyoffice.com/docs/docs-api/usage-api/config/#token
        // Defines the encrypted signature added to the ONLYOFFICE Docs config in the form of a token.
        token: null,
        type: "desktop",
        width: "100%",
      },
    };
    config.token = await getToken(presentation, config)
    return config;
  }
}
